import apiService from "@/lib/api";
import { dedupeGet, invalidateDedupe } from "@/utils/apiDeduper";

const COMPLIANCE_BASE = "/api/v2/compliance";

/**
 * Compliance API client.
 *
 * Phase C ships the "Send to Compliance" wiring only:
 *   - sendFromRecruitment(): backed by the Phase B endpoint
 *     POST /api/v2/compliance/candidates/from-recruitment.
 *   - getChecklistDropdown(): targets GET /api/v2/compliance/checklists/dropdown,
 *     which is a Phase D backend deliverable and does NOT exist yet. Until Phase D
 *     lands this call resolves to an empty list so the modal degrades gracefully
 *     instead of throwing.
 *
 * List/detail calls for the compliance screens arrive with Phases D/E.
 */
/**
 * Fetch a protected file and hand it to the browser as a download.
 *
 * Pulled out because three call sites need it and each one getting its own
 * copy is how the error handling drifts. The JSON check matters: a refusal
 * comes back as an error envelope even though the request asked for a blob,
 * and without it the user receives a file containing the word "Forbidden".
 */
const saveComplianceDownload = async (url, fallbackName) => {
  const response = await apiService.getSecure(url, { responseType: "blob" });

  const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: response.headers?.["content-type"] });

  if (blob.type?.includes("application/json")) {
    const text = await blob.text();
    let message = "Failed to download";
    try {
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.detail || message;
    } catch {
      // Non-JSON body — keep the generic message.
    }
    throw new Error(message);
  }

  // The server names the file; this is only the fallback for a response that
  // somehow carries no Content-Disposition.
  const disposition = response.headers?.["content-disposition"] || "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plain = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : plain || fallbackName;

  const href = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(href);
  }
  return filename;
};

export const ComplianceService = {
  /**
   * Send a recruitment candidate to compliance: creates a linked
   * ComplianceCandidate, assigns a checklist, and emails the candidate.
   *
   * @param {Object} params
   * @param {string} params.candidateId - Recruitment Candidate UUID.
   * @param {string} params.checklistId - ComplianceChecklist UUID to assign.
   * @param {string} params.applicationId - The application this run belongs
   *   to. REQUIRED since Phase G: a run that cannot name its application
   *   cannot be attributed to a job by any surface downstream, so the server
   *   refuses the send with COMPLIANCE_APPLICATION_REQUIRED.
   * @param {string} [params.subject] - The recruiter's own subject line.
   * @param {string} [params.body] - The recruiter's own email body, with
   *   {{variables}}.
   *
   *   `subject` and `body` are OPTIONAL and travel together. Omit them and the
   *   server sends today's standard compliance mail, unchanged — which is what
   *   every existing caller does and why they need no edit.
   *
   *   Send `body` and the candidate gets that copy instead, carrying the link
   *   to their upload portal. The server refuses the whole call with
   *   COMPLIANCE_PORTAL_LINK_REQUIRED (400) if the rendered body has no portal
   *   link in it, and creates no compliance run — so the retry after fixing
   *   the wording is clean.
   *
   *   ⚠️ Only send these keys when the recruiter actually wrote something. The
   *   server distinguishes an absent key from an explicit null, and passing
   *   `body: null` from an uninitialised form state would take the new path
   *   with an empty message.
   * @returns {Promise<any>} API response body.
   */
  sendFromRecruitment: async ({ candidateId, checklistId, applicationId, subject, body }) => {
    try {
      const payload = { candidateId, checklistId, applicationId };
      // Added only when present, so an omitted body stays absent from the JSON
      // rather than arriving as null — see the warning above.
      if (subject !== undefined && subject !== null) payload.subject = subject;
      if (body !== undefined && body !== null) payload.body = body;
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/candidates/from-recruitment`, payload);
      return response.data;
    } catch (error) {
      console.error("Error while sending candidate to compliance:", error);
      throw error;
    }
  },

  /**
   * The compliance email, filled in, before anything is sent.
   *
   * Writes nothing on the server: no compliance run is created and no portal
   * token is minted, so opening the modal and pressing Cancel leaves nothing
   * behind.
   *
   * `body` comes back with every variable substituted EXCEPT
   * `{{compliance_portal_link}}` and `{{portal_expiry_date}}`, which describe a
   * token that does not exist until the send happens. Both arrive as literal
   * `{{tokens}}` for the UI to draw as placeholders — they are deliberately not
   * blanked, because the server renders an empty value as an em dash and a
   * preview showing a dash where the link belongs reads as fine.
   *
   * @param {Object} params
   * @param {string} params.candidateId - Recruitment Candidate UUID.
   * @param {string} params.checklistId - ComplianceChecklist UUID.
   * @param {string} params.applicationId - The application the run would belong to.
   * @returns {Promise<{success: boolean, templateFound: boolean, subject: string,
   *   body: string, variables: string[], candidateEmail: string}>}
   *   `templateFound: false` when the tenant has no compliance template — show
   *   the fallback rather than an empty editor.
   */
  getSendPreview: async ({ candidateId, checklistId, applicationId }) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/send-preview`, {
        params: { candidateId, checklistId, applicationId },
      });
      return response.data;
    } catch (error) {
      console.error("Error while loading the compliance email preview:", error);
      throw error;
    }
  },

  /**
   * Fetch checklists for the "pick a checklist" dropdown.
   *
   * NOTE: the backend endpoint (GET /checklists/dropdown, guard
   * COMPLIANCE_MANAGE) lands in Phase D. Until then this returns an empty
   * array so the modal renders an empty picker rather than erroring.
   *
   * @returns {Promise<Array<{value: string, label: string}>>}
   */
  getChecklistDropdown: async () => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/dropdown`);
      const data = response.data;
      const list = data?.checklists ?? data?.data ?? data ?? [];
      return (Array.isArray(list) ? list : []).map((c) => ({
        value: c.value ?? c.id,
        label: c.label ?? c.name ?? c.title,
      }));
    } catch (error) {
      // Endpoint not yet available (Phase D) — degrade to an empty list.
      if (error?.response?.status === 404) return [];
      console.error("Error while fetching checklist dropdown:", error);
      throw error;
    }
  },

  // ── Compliance candidate list + stats (Phase D, COMPLIANCE_VIEW) ────────────

  /**
   * Paginated compliance-candidate list. Shape matches DataTableNew
   * (res.data.candidates + res.data.pagination); pass dataPath="candidates".
   */
  getCandidates: async ({ page = 1, per_page = 10, pagination = true, search = "", ...rest } = {}) => {
    const params = {
      page,
      per_page,
      pagination,
      ...(search ? { search } : {}),
      ...rest,
    };
    const key = `getCandidates_${JSON.stringify(params)}`;
    try {
      return await dedupeGet(key, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/candidates`, { params });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance candidates:", error);
      throw error;
    }
  },

  /** Compliance-candidate counts by status (list header cards). */
  getStats: async () => {
    try {
      return await dedupeGet("getStats", async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/stats`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance stats:", error);
      throw error;
    }
  },

  /** Dashboard live activity feed (which document of which candidate changed). */
  getActivity: async (limit = 20, from = undefined) => {
    const params = { limit, ...(from ? { from } : {}) };
    try {
      return await dedupeGet(`getActivity_${limit}_${from ?? ""}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/dashboard/activity`, {
          params,
        });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance activity:", error);
      throw error;
    }
  },

  // ── Dashboard v2: "Needs Attention" worklists + funnel (Phase 2A) ────────────
  // Separate compliance-only endpoints (not the recruitment dashboard APIs). Both
  // are read-only, COMPLIANCE_VIEW, all-time current-state snapshots.

  /**
   * The three prioritised "Needs Attention" worklists for the dashboard:
   * oldest pending-review documents, stuck re-requested candidates, and
   * candidates who never uploaded. Each list is `{ items, total,
   * intakeStatusFilter }` — the frontend shows Top-N + a "view all" link into a
   * Document Intake pre-filtered by `intakeStatusFilter`.
   *
   * @param {number} limit - Max rows per worklist (Top-N). Default 5.
   * @returns {Promise<{success, pendingReview, stuck, neverUploaded, stuckThresholdDays}>}
   */
  getAttention: async (limit = 5) => {
    try {
      return await dedupeGet(`getAttention_${limit}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/dashboard/attention`, {
          params: { limit },
        });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance attention worklists:", error);
      throw error;
    }
  },

  // ── Tenant compliance settings (Phase L, D13) ──────────────────────────────

  /** The tenant's compliance settings (trigger status). VIEW-readable. */
  getSettings: async () => {
    try {
      return await dedupeGet("getSettings", async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/settings`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance settings:", error);
      throw error;
    }
  },

  /** Update the tenant's compliance settings (COMPLIANCE_MANAGE). */
  updateSettings: async (payload) => {
    try {
      const response = await apiService.putSecure(`${COMPLIANCE_BASE}/settings`, payload);
      invalidateDedupe("getSettings");
      // Blunt clear: a settings save recomputes every candidate's status, so
      // any cached compliance read may now be stale, not just getSettings.
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while updating compliance settings:", error);
      throw error;
    }
  },

  /**
   * Compliance state for a RECRUITMENT candidate id — the profile indicator
   * (Phase 7B). Returns `applicable: false` when nothing should render.
   *
   * Note the path: `/recruitment-candidates/`, not `/candidates/`. Every other
   * `/candidates/{id}` compliance route takes a COMPLIANCE candidate id, and
   * passing one where the other is expected returns a confidently wrong answer.
   */
  getCandidateComplianceState: async (candidateId, applicationId = null) => {
    try {
      // `applicationId` is part of the key, not just the request. The answer
      // differs per application — one owns the run, the other is told so — and
      // a key on candidateId alone would serve the first caller's answer to the
      // second, which is exactly the "In compliance for <Job>" pill appearing
      // on the job that actually owns it.
      return await dedupeGet(`getCandidateComplianceState_${candidateId}_${applicationId ?? "none"}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/recruitment-candidates/${candidateId}/state`, {
          params: applicationId ? { application_id: applicationId } : undefined,
        });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching candidate compliance state:", error);
      throw error;
    }
  },

  /**
   * Reject the CANDIDATE, with a mandatory reason per failing document (7C).
   *
   * Replaces rejectDocument in the UI: a document problem is a re-request, a
   * candidate problem is a rejection. `reasons` is
   * `[{ documentId, reason }, ...]` and must be non-empty with every reason
   * non-blank — the server enforces both, so don't send a placeholder.
   */
  rejectCandidate: async (complianceCandidateId, reasons) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/candidates/${complianceCandidateId}/reject`, { reasons });
      // The rejection settles the case and can move recruitment applications,
      // so every cached compliance read is stale. Blunt invalidation matches
      // the established route for parameterised keys in this file.
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while rejecting compliance candidate:", error);
      throw error;
    }
  },

  /**
   * End a compliance run without rejecting the candidate (Phase C).
   *
   * Compliance is candidate-grained, so one live run blocks every other job
   * from starting one. Release is how that ends when the candidate has done
   * nothing wrong — transferred, withdrawn, the role closed, or the run was
   * started twice.
   *
   * `reason` is one of the five presets and `note` is required alongside it,
   * on every preset including `other`. The server enforces both, so a blank
   * note is a 422 rather than a release with no explanation.
   */
  releaseCandidate: async (complianceCandidateId, { reason, note }) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/candidates/${complianceCandidateId}/release`, { reason, note });
      // The run leaves every live surface at once — the intake list, the stat
      // cards, the worklist and the candidate profile indicator. Blunt
      // invalidation, matching `rejectCandidate` above.
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while releasing compliance candidate:", error);
      throw error;
    }
  },

  /**
   * Which jobs use this checklist as their default (Phase D).
   *
   * Backs the builder's job picker. Keyed on the checklist id, so two
   * checklists open in sequence do not serve each other's answer.
   */
  /**
   * Every job the builder's picker may offer, and who already holds each.
   *
   * The write refuses a job another checklist defaults for (409
   * COMPLIANCE_JOB_DEFAULT_TAKEN), so the picker reads this to grey those rows
   * and name their holder BEFORE the officer picks one. Pass the checklist
   * being edited and its own jobs come back unowned — otherwise the rows it
   * needs to untick would be the ones disabled.
   *
   * Not deduped on the checklist id alone: the create case passes none, and a
   * key that collapsed "no checklist" with a real one would serve the wrong
   * ownership answer to whichever opened second.
   */
  /**
   * The Compliance tab for one recruitment candidate (Phase F).
   *
   * Checklist header, per-candidate progress and the approved-document folders
   * — the ACTIVE run only. A released run keeps its documents but leaves every
   * live surface, so a candidate whose runs are all released answers
   * `hasRun: false` and the tab reads as "not currently in compliance".
   */
  getCandidateComplianceTab: async (candidateId, applicationId = null) => {
    try {
      // `applicationId` is part of the KEY, not just the request (Phase G).
      // The answer differs per application — whether this one owns the run —
      // and `dedupeGet` caches the promise, so a shared key would serve the
      // owning recruiter the other application's answer.
      return await dedupeGet(`getCandidateComplianceTab_${candidateId}_${applicationId ?? "none"}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/recruitment-candidates/${candidateId}/compliance-tab`, {
          params: applicationId ? { application_id: applicationId } : undefined,
        });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching the candidate compliance tab:", error);
      throw error;
    }
  },

  /**
   * Download one approved document, or a zip of them (Phase F).
   *
   * An authenticated blob fetch and not a bare href: these routes sit behind
   * `candidate.download_documents`, and a plain link carries no Authorization
   * header — it would 401 and hand the user a downloaded error page. Same
   * shape `candidateService.downloadFormattedResume` uses for the same reason.
   *
   * The filename comes from the server's `Content-Disposition` (RFC 5987, so a
   * candidate's name survives commas and non-ASCII). The client never builds
   * its own: one naming function, and the single file and the zip can never
   * disagree about what something is called.
   *
   * `section` omitted means everything approved, with the folders preserved;
   * naming it narrows the zip to one folder.
   */
  downloadCandidateDocument: async (candidateId, documentId, fallbackName) => {
    const url = `${COMPLIANCE_BASE}/recruitment-candidates/${candidateId}/documents/${documentId}/download`;
    return saveComplianceDownload(url, fallbackName || "document");
  },

  downloadCandidateDocumentsZip: async (candidateId, section, fallbackName) => {
    const url =
      `${COMPLIANCE_BASE}/recruitment-candidates/${candidateId}/documents/download-zip` + (section ? `?section=${encodeURIComponent(section)}` : "");
    return saveComplianceDownload(url, fallbackName || "compliance-documents.zip");
  },

  getChecklistJobOptions: async (checklistId) => {
    try {
      const key = checklistId ? `getChecklistJobOptions_${checklistId}` : "getChecklistJobOptions_new";
      return await dedupeGet(key, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklist-job-options`, {
          params: checklistId ? { checklist_id: checklistId } : {},
        });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklist job options:", error);
      throw error;
    }
  },

  getChecklistJobs: async (checklistId) => {
    try {
      return await dedupeGet(`getChecklistJobs_${checklistId}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}/jobs`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklist jobs:", error);
      throw error;
    }
  },

  /**
   * Make this checklist the default for exactly these jobs (Phase D).
   *
   * A replace, not an append: `jobIds` is the complete set, so a job left out
   * is unlinked and an empty array clears every link. Its own endpoint rather
   * than part of the checklist save — a checklist edit runs through the
   * re-apply wizard, and a job default resets no documents, so it has no
   * business in that flow.
   */
  setChecklistJobs: async (checklistId, jobIds) => {
    try {
      const response = await apiService.putSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}/jobs`, { jobIds });
      // Both directions of the link are cached: the builder's picker and
      // job-detail's card. Blunt invalidation, as everywhere else in this file.
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while setting checklist jobs:", error);
      throw error;
    }
  },

  /**
   * The checklist a job's candidates are offered by DEFAULT (Phase D).
   *
   * A default, not an assignment — the recruiter can still pick another for a
   * candidate whose requirements differ. Returns `{ checklistId: null }` when
   * the job has no default, or when its default was deleted or retired; that
   * is the ordinary case, so callers render an empty state rather than an
   * error.
   */
  getJobChecklist: async (jobId) => {
    try {
      return await dedupeGet(`getJobChecklist_${jobId}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/jobs/${jobId}/checklist`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching the job's default checklist:", error);
      throw error;
    }
  },

  // ── Analytics (Phase 2B — five metric families, COMPLIANCE_VIEW) ────────────
  // Read-only, tenant-scoped, live aggregation. Each family is its own endpoint
  // under /analytics/*; all accept an optional date window (`from`/`to`,
  // YYYY-MM-DD) + client `tz` for day/week bucketing. Kept separate from the
  // recruitment analytics + the 2A /dashboard/* endpoints.

  /**
   * Build the shared analytics query params. `range` = { from, to } (either may
   * be omitted → unbounded that side); `tz` defaults to the browser timezone so
   * day/week buckets line up with what the leader sees locally.
   */
  _analyticsParams: (range = {}, tz) => {
    const params = {};
    if (range?.from) params.from = range.from;
    if (range?.to) params.to = range.to;
    params.tz = tz || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC") || "UTC";
    return params;
  },

  /**
   * Cross-family attention strip (added 2026-08-01): one trigger per family —
   * SLA breach + named bottleneck stage, reviewer outlier, AI accuracy floor +
   * confidence inversion, worst quality slot — surfaced only when its own
   * condition fires in range. Empty `items` = a normal "all clear" state.
   */
  getAnalyticsAttention: async (range = {}, tz) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/attention`, {
        params: ComplianceService._analyticsParams(range, tz),
      });
      return response.data;
    } catch (error) {
      console.error("Error while fetching analytics attention strip:", error);
      throw error;
    }
  },

  /** F1 — process health & throughput (KPIs + rate/volume trends). */
  getAnalyticsOverview: async (range = {}, tz) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/overview`, {
        params: ComplianceService._analyticsParams(range, tz),
      });
      return response.data;
    } catch (error) {
      console.error("Error while fetching analytics overview:", error);
      throw error;
    }
  },

  /** F2 — bottleneck & delay analysis (stage durations, WIP, aging, SLA). */
  getAnalyticsBottlenecks: async (range = {}, tz) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/bottlenecks`, {
        params: ComplianceService._analyticsParams(range, tz),
      });
      return response.data;
    } catch (error) {
      console.error("Error while fetching analytics bottlenecks:", error);
      throw error;
    }
  },

  /** F3 — reviewer activity & workload (audit-trail derived; tenant baseline). */
  getAnalyticsReviewers: async (range = {}, tz) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/reviewers`, {
        params: ComplianceService._analyticsParams(range, tz),
      });
      return response.data;
    } catch (error) {
      console.error("Error while fetching analytics reviewers:", error);
      throw error;
    }
  },

  /** F4 — AI extraction accuracy vs officer-confirmed ground truth. */
  getAnalyticsAiAccuracy: async (range = {}, tz) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/ai-accuracy`, {
        params: ComplianceService._analyticsParams(range, tz),
      });
      return response.data;
    } catch (error) {
      console.error("Error while fetching analytics AI accuracy:", error);
      throw error;
    }
  },

  /** F5 — document & checklist quality (slot rework, funnels, portal). */
  getAnalyticsQuality: async (range = {}, tz) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/quality`, {
        params: ComplianceService._analyticsParams(range, tz),
      });
      return response.data;
    } catch (error) {
      console.error("Error while fetching analytics quality:", error);
      throw error;
    }
  },

  /**
   * Download one analytics view in one format. `view` ∈ overview | bottlenecks |
   * reviewers | ai-accuracy | quality; `format` ∈ csv | pdf.
   *
   * One function for both, because everything except the extension and the MIME
   * type is identical — the params, the Content-Disposition filename and the
   * anchor-click dance. Two copies would drift the moment one gained a param.
   */
  _exportAnalytics: async (view, format, range = {}, tz) => {
    const MIME = {
      csv: "text/csv;charset=utf-8;",
      pdf: "application/pdf",
    };
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/analytics/${view}/export.${format}`, {
        params: ComplianceService._analyticsParams(range, tz),
        responseType: "blob",
      });
      // Prefer the server-provided filename (Content-Disposition), else derive.
      const disposition = response?.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match ? match[1] : `compliance-${view}.${format}`;
      const blob = new Blob([response.data], { type: MIME[format] });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error(`Error while exporting analytics ${format.toUpperCase()}:`, error);
      throw error;
    }
  },

  // ── Compliance Audit Log page ───────────────────────────────────────────────
  //
  // The tenant-wide trail behind /admin/compliance/audit-log. COMPLIANCE_AUDIT_VIEW
  // on every route; the tenant comes from the JWT, so none of these sends a
  // tenant id. Responses are camelCase (the routes set response_model_by_alias).

  /**
   * One page of the tenant's compliance audit trail.
   *
   * `params` are passed through as the API names them — action, entityType,
   * actorType, complianceCandidateId, fromDate, toDate, search, page, perPage,
   * sortOrder. Empty values are dropped rather than sent blank, because an
   * empty `action=` reads on the server as a filter matching nothing.
   *
   * Returns `{ success, data: { pagination, items } }`.
   */
  getAuditLog: async (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
    const key = `getComplianceAuditLog_${JSON.stringify(clean)}`;
    try {
      return await dedupeGet(key, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/audit-log`, { params: clean });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance audit log:", error);
      throw error;
    }
  },

  /** Distinct actions + entity types this tenant's trail contains, for the filter dropdowns. */
  getAuditLogFilterOptions: async () => {
    try {
      return await dedupeGet("getComplianceAuditFilterOptions", async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/audit-log/filter-options`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching compliance audit filter options:", error);
      throw error;
    }
  },

  /**
   * Download the filtered trail as a spreadsheet.
   *
   * Same filters as `getAuditLog` and no paging, so the file is what the screen
   * is showing rather than the page the reader happens to be on. Deliberately
   * NOT behind `dedupeGet`: a cached promise would hand back a stale file.
   */
  exportAuditLog: async (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/audit-log/export.xlsx`, {
        params: clean,
        responseType: "blob",
      });
      // The route sends RFC 5987 (`filename*=UTF-8''…`); the analytics exports
      // send the plain form. Read both, so this keeps working if either changes.
      const disposition = response?.headers?.["content-disposition"] || "";
      const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const plain = /filename="?([^";]+)"?/.exec(disposition);
      const filename = encoded ? decodeURIComponent(encoded[1]) : plain ? plain[1] : "compliance-audit-log.xlsx";

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error while exporting compliance audit log:", error);
      throw error;
    }
  },

  /** Download one analytics view as CSV — the numbers, for a spreadsheet. */
  exportAnalyticsCsv: (view, range = {}, tz) => ComplianceService._exportAnalytics(view, "csv", range, tz),

  /** Download one analytics view as PDF — the charts and numbers, for reading. */
  exportAnalyticsPdf: (view, range = {}, tz) => ComplianceService._exportAnalytics(view, "pdf", range, tz),

  // ── Checklist CRUD (Phase D, COMPLIANCE_MANAGE) ─────────────────────────────

  /**
   * All non-deleted checklists.
   *
   * Optional server-side narrowing (`search` / `region`) and paging (`page` /
   * `per_page`) are sent when given. The backend ignores them today (see
   * BACKEND_REQUIREMENTS #2), so the Checklists page still filters and pages
   * the returned list in memory; once the backend honours them the page reads
   * `pagination` off the response and drops the client-side slice.
   *
   * No `document` narrowing: the response carries `sections: []` by decision
   * (loading the tree made this endpoint's cost scale with every document in
   * the tenant), so there is no document name here to narrow by.
   */
  getChecklists: async (params = {}) => {
    const query = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
    try {
      return await dedupeGet(`getChecklists_${JSON.stringify(query)}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists`, {
          params: query,
        });
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklists:", error);
      throw error;
    }
  },

  /**
   * The distinct regions that this tenant's checklists actually use.
   *
   * Backs the region filter dropdown on the Checklists and Document Intake
   * screens — the filter offers regions that have a checklist, not the whole
   * country list.
   */
  getChecklistRegions: async () => {
    try {
      return await dedupeGet("getChecklistRegions", async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/regions`);
        return response.data?.regions ?? [];
      });
    } catch (error) {
      console.error("Error while fetching checklist regions:", error);
      throw error;
    }
  },

  /** A single checklist with its full section→subsection→field tree. */
  getChecklist: async (checklistId) => {
    try {
      return await dedupeGet(`getChecklist_${checklistId}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklist:", error);
      throw error;
    }
  },

  /**
   * Who is mid-process against a checklist, and what an edit would touch.
   *
   * Answers "is it safe to edit this?". Editing hard-deletes removed
   * subsections and the document FK is ON DELETE SET NULL, so uploaded
   * documents lose their link silently — `editRisk` is true whenever there are
   * in-flight candidates or attached documents.
   *
   * Returns `{checklistId, checklistName, version, candidateCount,
   * inFlightCount, statusCounts, attachedDocumentCount, editRisk, candidates}`.
   */
  getChecklistUsage: async (checklistId) => {
    try {
      return await dedupeGet(`getChecklistUsage_${checklistId}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}/usage`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklist usage:", error);
      throw error;
    }
  },

  /**
   * The tenant's country document sets — the sources a checklist is built from
   * (ATH-I1765).
   *
   * A master is never assignable, so it never appears in the assign dropdown.
   * The builder reads this when an officer picks a country, to offer that
   * country's documents as a starting point.
   *
   * Deduped: the builder calls it on every country change and the list screen
   * renders it, so two surfaces asking within the 2s window share one request.
   *
   * Returns `{masters: [{id, name, region, countryCode, isCountryMaster,
   * sectionCount, subsectionCount, totalWeight, isActive}]}`.
   */
  getChecklistMasters: async () => {
    try {
      return await dedupeGet("getChecklistMasters", async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/masters`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklist masters:", error);
      throw error;
    }
  },

  /**
   * Acknowledge the review notice on a country document set (CM-7).
   *
   * The notice is raised when a SuperAdmin reticks a country the tenant had
   * taken away, or when the platform's set for that country has changed since
   * this copy was seeded. Both are things the officer must have *seen*, so it
   * clears on an explicit action rather than expiring on its own.
   *
   * Invalidates the masters cache: without that the 2s dedupe window serves
   * the pre-dismiss response straight back and the notice reappears on the
   * next render, which reads as the button being broken.
   */
  dismissMasterReview: async (checklistId) => {
    try {
      const response = await apiService.postSecure(
        `${COMPLIANCE_BASE}/checklists/${checklistId}/master-review/dismiss`
      );
      invalidateDedupe("getChecklistMasters");
      return response.data;
    } catch (error) {
      console.error("Error while dismissing the country set review notice:", error);
      throw error;
    }
  },

  /** Create a checklist with its tree. */
  createChecklist: async (payload) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/checklists`, payload);
      // The list/detail reads are deduped; a write must drop them or the
      // refetch that follows a save can be served the pre-save response.
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while creating checklist:", error);
      throw error;
    }
  },

  /** Update a checklist; when `sections` is present the whole tree is replaced. */
  updateChecklist: async (checklistId, payload) => {
    try {
      const response = await apiService.putSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}`, payload);
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while updating checklist:", error);
      throw error;
    }
  },

  /**
   * Immutable version history for a checklist (Phase 5A/5F).
   *
   * Each version carries its full tree and change summary, so the modal renders
   * everything from one call rather than N. Deduped per checklist.
   */
  getChecklistVersions: async (checklistId) => {
    try {
      return await dedupeGet(`getChecklistVersions_${checklistId}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}/versions`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching checklist versions:", error);
      throw error;
    }
  },

  /**
   * DRY RUN for a checklist edit (Phase 5D).
   *
   * Writes nothing, so the wizard can call it on every step and the officer can
   * move Back and forward without half-committing an edit. Never deduped: the
   * draft tree changes between calls, so a cached promise would answer a
   * question that was not asked.
   */
  previewChecklistReapply: async (checklistId, payload) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}/reapply/preview`, payload);
      return response.data;
    } catch (error) {
      console.error("Error while previewing checklist re-apply:", error);
      throw error;
    }
  },

  /**
   * Commit the edit AND apply it to the selected candidates (Phase 5D).
   *
   * `expectedVersion` is the lost-update guard: the server refuses with 409
   * COMPLIANCE_CHECKLIST_VERSION_CONFLICT when someone else saved in the
   * meantime, rather than silently resurrecting whatever they removed.
   */
  commitChecklistReapply: async (checklistId, payload) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/checklists/${checklistId}/reapply`, payload);
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while applying checklist update:", error);
      throw error;
    }
  },

  /** Soft-delete a checklist (blocked while assigned to any candidate). */
  deleteChecklist: async (checklistId) => {
    try {
      const response = await apiService.delete(`${COMPLIANCE_BASE}/checklists/${checklistId}`);
      invalidateDedupe();
      return response.data;
    } catch (error) {
      console.error("Error while deleting checklist:", error);
      throw error;
    }
  },

  // ── Officer document upload + review (Phase E) ──────────────────────────────

  /** Candidate detail: identity + assigned checklist tree + documents. */
  getCandidateDetail: async (complianceCandidateId) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/candidates/${complianceCandidateId}`);
      return response.data;
    } catch (error) {
      console.error("Error while fetching compliance candidate detail:", error);
      throw error;
    }
  },

  /** Weighted document-approval progress. */
  getCandidateProgress: async (complianceCandidateId) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/candidates/${complianceCandidateId}/progress`);
      return response.data;
    } catch (error) {
      console.error("Error while fetching compliance progress:", error);
      throw error;
    }
  },

  /**
   * Officer uploads a document (multipart).
   *
   * `attestationAccepted` is required by the server (Phase 8C) and defaults to
   * false here on purpose: a caller that forgets it gets a clear 400 rather
   * than silently writing a consent row nobody agreed to.
   */
  uploadDocument: async (complianceCandidateId, { file, documentCategory, subsectionId, attestationAccepted = false }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (documentCategory) formData.append("documentCategory", documentCategory);
      if (subsectionId) formData.append("subsectionId", subsectionId);
      formData.append("attestationAccepted", String(Boolean(attestationAccepted)));
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/candidates/${complianceCandidateId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error while uploading compliance document:", error);
      throw error;
    }
  },

  /** Fetch a stored document as a blob (for inline viewing). */
  fetchDocumentFile: async (documentId) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/documents/file/${documentId}`, {
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error("Error while fetching document file:", error);
      throw error;
    }
  },

  /** Upsert officer-confirmed field values for a document. */
  confirmFields: async (documentId, fields) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/documents/${documentId}/confirm-fields`, { fields });
      return response.data;
    } catch (error) {
      console.error("Error while confirming fields:", error);
      throw error;
    }
  },

  approveDocument: async (documentId) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/documents/${documentId}/approve`, null);
      return response.data;
    } catch (error) {
      console.error("Error while approving document:", error);
      throw error;
    }
  },

  rejectDocument: async (documentId, reason) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/documents/${documentId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error("Error while rejecting document:", error);
      throw error;
    }
  },

  rerequestDocument: async (documentId, description) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/documents/${documentId}/rerequest`, { description });
      return response.data;
    } catch (error) {
      console.error("Error while re-requesting document:", error);
      throw error;
    }
  },

  /**
   * Complete audit history for one compliance candidate: their own portal
   * uploads plus every officer action, across all of their documents
   * (including replaced and deleted ones), oldest-first.
   *
   * Replaces the per-*document* `getDocumentAuditHistory`, which was never
   * built server-side. A document-scoped trail could not show candidate-level
   * events, and it lost history whenever a document was superseded.
   *
   * Returns `{history, total, truncated}`. Errors propagate — the caller shows
   * "could not load" rather than an empty timeline, which would wrongly assert
   * that nothing ever happened.
   */
  getCandidateAuditLog: async (complianceCandidateId) => {
    try {
      return await dedupeGet(`getCandidateAuditLog_${complianceCandidateId}`, async () => {
        const response = await apiService.getSecure(`${COMPLIANCE_BASE}/candidates/${complianceCandidateId}/audit-log`);
        return response.data;
      });
    } catch (error) {
      console.error("Error while fetching candidate audit log:", error);
      throw error;
    }
  },

  getExtraction: async (documentId) => {
    try {
      const response = await apiService.getSecure(`${COMPLIANCE_BASE}/documents/${documentId}/extraction`);
      return response.data;
    } catch (error) {
      console.error("Error while fetching extraction:", error);
      throw error;
    }
  },

  retriggerExtraction: async (documentId) => {
    try {
      const response = await apiService.postSecure(`${COMPLIANCE_BASE}/documents/${documentId}/extraction/retrigger`, null);
      return response.data;
    } catch (error) {
      console.error("Error while retriggering extraction:", error);
      throw error;
    }
  },

  // ── Candidate self-service portal (Phase K, D10 — public, token-authed) ─────
  // No Bearer token: the URL token is the credential; the backend resolves the
  // tenant from it, so no tenant-slug header is needed either.

  /** Portal state for a candidate link: greeting + per-document slot status. */
  getPortal: async (token) => {
    try {
      const response = await apiService.get(`${COMPLIANCE_BASE}/portal/${encodeURIComponent(token)}`);
      return response.data;
    } catch (error) {
      console.error("Error while fetching compliance portal:", error);
      throw error;
    }
  },

  /**
   * Fetch a document the CANDIDATE uploaded, as a blob (Phase 8B).
   *
   * The portal had no preview at all: a candidate could upload a document and
   * never see what the officer received — so a wrong file, or a blurry scan,
   * was invisible to them until it came back as a re-request.
   *
   * `apiService.get`, not `getSecure`: the portal is token-authed with no
   * Bearer credential (Hard Rule 1). The backend scopes
   * `/portal/{token}/documents/file/{id}` strictly to the token's own
   * candidate, so a candidate cannot read anyone else's document by guessing an
   * id. This is frontend-only work — the endpoint already existed.
   */
  fetchPortalDocumentFile: async (token, documentId) => {
    try {
      return await apiService.get(`${COMPLIANCE_BASE}/portal/${encodeURIComponent(token)}/documents/file/${documentId}`, { responseType: "blob" });
    } catch (error) {
      console.error("Error while fetching portal document file:", error);
      throw error;
    }
  },

  /** Candidate uploads a document for one checklist slot (multipart). */
  uploadPortalDocument: async (token, { file, subsectionId, attestationAccepted = false }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("subsectionId", subsectionId);
      formData.append("attestationAccepted", String(Boolean(attestationAccepted)));
      const response = await apiService.post(`${COMPLIANCE_BASE}/portal/${encodeURIComponent(token)}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error while uploading portal document:", error);
      throw error;
    }
  },
};

export default ComplianceService;
